/**
 * PWA Install Prompt Component
 * Shows install button when browser supports PWA installation
 */

import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallPromptProps {
  variant?: 'banner' | 'button' | 'compact';
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ variant = 'button' }) => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setInstallPrompt(null);
  };

  // Don't show if already installed or no prompt available
  if (isInstalled) return null;

  // For iOS Safari, show instructions since it doesn't support beforeinstallprompt
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  if (!installPrompt && !isIOSSafari) return null;

  if (variant === 'banner' && showBanner) {
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        zIndex: 1000,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            Install Larry&apos;s Help
          </div>
          <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
            {isIOSSafari
              ? 'Tap Share, then "Add to Home Screen"'
              : 'Add to your home screen for quick access'}
          </div>
        </div>
        {!isIOSSafari && (
          <button
            onClick={handleInstallClick}
            style={{
              background: 'white',
              color: '#1e3a5f',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Install
          </button>
        )}
        <button
          onClick={() => setShowBanner(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '1.2rem',
            cursor: 'pointer',
            padding: '4px 8px',
            opacity: 0.8,
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={isIOSSafari ? undefined : handleInstallClick}
        title={isIOSSafari ? 'Tap Share → Add to Home Screen' : 'Install App'}
        style={{
          background: 'transparent',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '6px 10px',
          cursor: isIOSSafari ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '0.85rem',
          color: '#374151',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Install
      </button>
    );
  }

  // Default button variant
  return (
    <button
      onClick={isIOSSafari ? undefined : handleInstallClick}
      style={{
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '8px',
        fontWeight: 600,
        cursor: isIOSSafari ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '1rem',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {isIOSSafari ? 'Share → Add to Home Screen' : 'Install App'}
    </button>
  );
};

export default PWAInstallPrompt;
